import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Calendar, ChevronDown, ChevronUp, Dumbbell, Target, Timer } from "lucide-react"
import { DateTime } from "luxon"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import ExercisesDropdown from "../components/ExercisesDropdown"
import ProgressChart from "../components/ProgressChart"
import { CardDescription } from "../components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Skeleton } from "../components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { useExercises } from "../hooks/useExercises"
import { useActiveWorkoutStore } from "../store"
import { apiFetch } from "../utils/apiFetch"
import {
  calculateExerciseWeight,
  calculateTotalWeight,
  displayDuration,
  type TExercise,
  type TWorkout,
} from "../utils/workoutUtils"

export const Route = createFileRoute("/_app/history")({
  component: WorkoutHistory,
})

function WorkoutHistory() {
  const [expandedWorkout, setExpandedWorkout] = useState<string | null>(null)
  const [selectedExercise, setSelectedExercise] = useState<string>("")
  const [timeRange, setTimeRange] = useState<"weeks" | "months" | "all">("all")
  const { activeWorkout } = useActiveWorkoutStore((state) => state)

  const { data: exercises } = useExercises()

  const { data: workouts, isLoading } = useQuery({
    queryKey: ["workouts"],
    queryFn: async () => {
      const response = await apiFetch(`/v1/workouts`, {
        method: "GET",
      })
      return response as Array<TWorkout>
    },
    initialData: [],
  })

  // const sortedWorkouts = workouts
  //   ?.filter((workout) => workout.dateCompleted)
  //   .sort(
  //     (a, b) =>
  //       DateTime.fromISO(b.dateScheduled).diff(
  //         DateTime.fromISO(a.dateScheduled),
  //       ).days,
  //   );

  const descWorkouts = workouts?.filter((workout) => workout.dateCompleted).toReversed()

  const toggleExpand = (workoutId: string) => {
    setExpandedWorkout(expandedWorkout === workoutId ? null : workoutId)
  }

  // Get unique exercise names across all workouts
  // const exerciseOptions = Array.from(
  //   new Set(
  //     workouts
  //       ?.flatMap((workout) => workout.exercises)
  //       .map((exercise) => exercise.name),
  //   ),
  // ).sort();

  // if (isLoading) {
  //   return <p>Loading...</p>;
  // }

  // if (!isLoading && sortedWorkouts?.length === 0) {
  //   return (
  //     <div className="space-y-6">
  //       <Card className="workout-card">
  //         <CardHeader>
  //           <CardTitle>Workout History</CardTitle>
  //         </CardHeader>
  //         <CardContent>
  //           <p className="text-muted-foreground">
  //             No completed workouts yet. Complete a workout to see it here!
  //           </p>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="charts">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="charts">Charts</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <Select value={timeRange} onValueChange={(value) => setTimeRange(value as any)}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weeks">Recent</SelectItem>
              <SelectItem value="months">Monthly</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="charts" className="space-y-6">
          {isLoading ? (
            <Card className="workout-card">
              <CardHeader className="pb-2">
                <CardTitle>Weekly Total Weight Lifted</CardTitle>
                <CardDescription>Summary of total weight lifted each week</CardDescription>
              </CardHeader>
              <CardContent>
                <Skeleton className="h-72 w-full" />
              </CardContent>
            </Card>
          ) : (
            <ProgressChart workouts={workouts} timeRange={timeRange} />
          )}

          <Card className="workout-card">
            <CardHeader>
              <CardTitle>Exercise Progress</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="mb-4 h-72 w-full" />
              ) : (
                <div className="mb-4">
                  <ExercisesDropdown className="w-full" value={selectedExercise} onValueChange={setSelectedExercise} />
                </div>
              )}
              {selectedExercise && (
                <>
                  {exercises && exercises.length > 0 && exercises.find((x) => x.name === selectedExercise) && (
                    <p className="mb-2 text-muted-foreground text-sm">
                      Max: {exercises?.find((x) => x.name === selectedExercise)?.maxWeightLifted?.maxWeight} lbs for{" "}
                      {exercises?.find((x) => x.name === selectedExercise)?.maxWeightLifted?.reps} rep(s)
                    </p>
                  )}
                  <ProgressChart workouts={workouts} exerciseFilter={selectedExercise} timeRange={timeRange} />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <Card className="workout-card" key={index}>
                <CardContent className="space-y-4">
                  <Skeleton className="h-15 w-full" />
                </CardContent>
              </Card>
            ))}
          {descWorkouts?.map((workout) => {
            const isMatching = activeWorkout && workout.name === activeWorkout.name
            return (
              <Card key={workout.workoutId} className={`workout-card ${isMatching ? "border-primary/60" : ""}`}>
                <div
                  className="flex cursor-pointer items-center justify-between p-4"
                  onClick={() => toggleExpand(workout.workoutId)}
                >
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      {expandedWorkout === workout.workoutId ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </Button>
                    <div>
                      <h3 className="flex items-center gap-2 font-medium text-lg">
                        {workout.name}
                        {isMatching && (
                          <Target size={14} className="text-primary" aria-label="Matches current workout" />
                        )}
                      </h3>
                      <div className="flex space-x-4 text-muted-foreground text-sm">
                        <span className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {DateTime.fromISO(workout.dateScheduled).toLocaleString(DateTime.DATE_MED)}
                        </span>
                        <span className="flex items-center">
                          <Dumbbell size={14} className="mr-1" />
                          {workout.exercises.length} exercises
                        </span>
                        <span className="flex items-center">
                          <Timer size={14} className="mr-1" />
                          {displayDuration(workout.dateStarted!, workout.dateCompleted!)}
                        </span>
                        <span className="flex items-center font-medium text-workout-secondary">
                          {calculateTotalWeight(workout).toLocaleString()} lbs total
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {expandedWorkout === workout.workoutId && (
                  <CardContent>
                    <div className="space-y-3">
                      {workout.exercises.map((exercise: TExercise) => (
                        <div key={exercise.exerciseId} className="border-border border-t pt-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">{exercise.name}</h4>
                            <span className="font-medium text-sm text-workout-secondary">
                              {calculateExerciseWeight(exercise).toLocaleString()} lbs total
                            </span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-muted-foreground text-sm sm:grid-cols-3">
                            {exercise.sets
                              .filter((x) => x.isCompleted)
                              .map((set, index) => (
                                <div key={set.setId} className="rounded-md bg-muted/30 px-3 py-2">
                                  Set {index + 1}: {set.weight} lbs × {set.reps} reps
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}

                      {workout.notes && (
                        <div className="mt-3 border-border border-t pt-3">
                          <h4 className="font-medium">Notes</h4>
                          <p className="mt-1 text-muted-foreground">{workout.notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </TabsContent>
      </Tabs>
    </div>
  )
}
